import './FilteredNewsTile.scss';
import { Heading } from '@lmig/lmds-react-typography';
import { useEffect, useState } from 'react';
import { useUserData } from 'Foundation/User/UserContext';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { Link } from '@lmig/lmds-react-link';
import { useLocalStorage, useSessionStorage } from 'Utilities/UseStorage';
import globalConfig from 'Data/globalConfig.json';

interface FilteredNewsTileProps {
  articleTypes?: string[];
  title?: string;
}

export function FilteredNewsTile(props: FilteredNewsTileProps) {
  const { userData } = useUserData();
  const newsSearchResultsURL = `${globalConfig.Coveo.searchPage}?qtype=news`;
  const sessionStorageKey = "LatestNewsArticlesData";
  const expirationMinutes: number = 60;
  const [latestNewsArticles, setLatestNewsArticles] = useSessionStorage<NewsArticleLink[]>(
    sessionStorageKey,
    undefined,
    expirationMinutes,
  );
  const [viewedNewsArticles, setViewedNewsArticles] = useLocalStorage<NewsArticleLink[]>(sessionStorageKey, []);
  const [filteredArticles, setFilteredArticles] = useState<NewsArticleLink[]>();
  const [isLoaded, setIsLoaded] = useState<boolean>();
  const title = props?.title && props.title.length > 0 ? props.title : 'My news';

  useEffect(() => {
    if (!isLoaded && !latestNewsArticles) getLatestArticles();
  }, [isLoaded]);

  useEffect(() => {
    if (userData?.state && latestNewsArticles) {
      let displayArticles = getUnseenArticles();
      if (displayArticles.length < 4) {
        let viewed = getViewedArticles();
        displayArticles = displayArticles.concat(viewed);
      }
      setFilteredArticles(displayArticles?.slice(0, 4));
    }
  }, [userData?.state, latestNewsArticles]);

  const matchesArticleTypes = (article: NewsArticleLink): boolean => {
    if (!props?.articleTypes || props.articleTypes.length === 0) {
      return true; // No filter specified, show all
    }
    // Check if article has newsTypes array, check if any match
    if (article.newsTypes && Array.isArray(article.newsTypes)) {
      return article.newsTypes.some((type: string) => props.articleTypes?.includes(type));
    }
    return true; // If no type field exists, include the article
  };

  const getUnseenArticles = (): NewsArticleLink[] => {
    if (userData?.state && latestNewsArticles) {
      return (
        latestNewsArticles
          ?.filter((w) => !viewedNewsArticles?.map((a) => a.id).includes(w.id))
          .filter((x) => x.states?.includes(userData?.state))
          .filter((article) => matchesArticleTypes(article))
          .sort((y, z) => new Date(z.publishDate).getTime() - new Date(y.publishDate).getTime()) ?? []
      );
    } else {
      return [];
    }
  };

  const getViewedArticles = (): NewsArticleLink[] => {
    if (userData?.state && latestNewsArticles) {
      return (
        viewedNewsArticles
          ?.filter((x) => x.states?.includes(userData?.state))
          .filter((article) => matchesArticleTypes(article))
          .sort((y, z) => new Date(z.publishDate).getTime() - new Date(y.publishDate).getTime()) ?? []
      );
    } else {
      return [];
    }
  };

  const getLatestArticles = async () => {
    setIsLoaded(false);
    const queryString =
      props?.articleTypes && props.articleTypes.length > 0
        ? `?articletypes="${props.articleTypes.join(`","`)}"` 
        : '';
    let response = (await SitecoreFetch(
      `/api/sitecore/CoveoSearchAsync/GetNewsArticleLinks${queryString}`,
    )) as IServerResponse;
    let result: NewsArticleLink[];
    if (response.Status.Code === HttpStatusCodes.OK) {
      result = response.Data as NewsArticleLink[];
    } else result = [];
    setLatestNewsArticles({ data: result });
    setIsLoaded(true);
  };

  const markBeenViewed = (id: string) => () => {
    if (latestNewsArticles) {
      let viewed: NewsArticleLink | undefined = latestNewsArticles.find((article) => article.id == id);
      if (viewed) {
        let alreadyViewedArticles: NewsArticleLink[] = viewedNewsArticles;
        if (!alreadyViewedArticles) alreadyViewedArticles = [];
        if (!alreadyViewedArticles.map((a) => a.id).includes(viewed.id)) {
          alreadyViewedArticles.push(viewed);
          setViewedNewsArticles({ data: alreadyViewedArticles });
        }
      }
    }
  };

  return (
    <div id="news-tile-container">
      <div className="header">
        <Heading type="h4-light" className="title">
          {title}
        </Heading>
        <Link href={newsSearchResultsURL} className="see-all-link lm-link" variant="standalone">
          See all
        </Link>
      </div>
      <div className="latest-news-articles">
        {filteredArticles?.length ? (
          filteredArticles?.map(
            (article) =>
              article?.headline && (
                <div>
                  <Link
                    href={article.uri}
                    onClick={markBeenViewed(article.id)}
                    className="news-article-link"
                    variant="standalone"
                    caretPosition="none"
                  >
                    {article.featuredImageSrc && <img src={article.featuredImageSrc} />}
                    <div>{article.headline}</div>
                  </Link>
                  <hr />
                </div>
              ),
          )
        ) : (
          <div className="no-latest-articles">No recent news articles.</div>
        )}
      </div>
    </div>
  );
}

export default FilteredNewsTile;
